import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Clock,
  ShieldCheck,
  UserRound,
  HandCoins,
  Fingerprint,
  Headphones,
  ChevronDown,
  ArrowRight,
  MapPin,
  Mail,
  CheckCircle2,
  Wifi,
  Printer,
  Hexagon,
  FolderOpen,
  User,
  ShieldAlert,
  Laptop,
  Search,
  Smartphone,
  KeyRound,
  Accessibility,
  Wrench,
  Heart,
  MessageCircle,
  Home,
  Users,
  DollarSign,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import managedItHero from "@/assets/managed-it-hero.webp";
import techSupportHero from "@/assets/tech-support-hero.webp";
import voipHero from "@/assets/voip-hero.png";
import seniorsHero from "@/assets/seniors-tech-help.png";

const HERO_IMAGES: Record<string, string> = {
  "tech-support": techSupportHero,
  voip: voipHero,
  "tech-support-for-seniors": seniorsHero,
};
import serviceBg from "@/assets/service-bg.webp";
import whiteBandsBg from "@/assets/white-bands-bg.webp";
import onboardingBg from "@/assets/onboarding-bg.webp";
import armandsHeadshot from "@/assets/armands-headshot.png";
import { services, type Service } from "@/data/services";
import { cityList } from "@/data/cities";
import { locationCities } from "@/data/location-cities";
import techSupportFaqsData from "@/data/tech-support-faqs.json";
import voipFaqsData from "@/data/voip-faqs.json";
import seniorsFaqsData from "@/data/tech-support-for-seniors-faqs.json";
import adaFaqsData from "@/data/ada-compliant-website-faqs.json";
import cyberFaqsData from "@/data/cyber-security-faqs.json";

const TECH_SUPPORT_FAQS = techSupportFaqsData.faqs;
const VOIP_FAQS = voipFaqsData.faqs;
const SENIORS_FAQS = seniorsFaqsData.faqs;
const ADA_FAQS = adaFaqsData.faqs;
const CYBER_FAQS = cyberFaqsData.faqs;

type Props = { service: Service };

export function ServicePageTemplate({ service }: Props) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const name = service.shortTitle;
  const nameLower = name.toLowerCase();
  const otherServices = services.filter((s) => s.slug !== service.slug).slice(0, 6);

  const faqs =
    service.slug === "tech-support"
      ? TECH_SUPPORT_FAQS
      : service.slug === "voip"
      ? VOIP_FAQS
      : service.slug === "tech-support-for-seniors"
      ? SENIORS_FAQS
      : service.slug === "ada-compliant-website"
      ? ADA_FAQS
      : service.slug === "cyber-security"
      ? CYBER_FAQS
      : [
          {
            q: `What are ${nameLower} services?`,
            a: `${service.description} For Montclair-area businesses, this means less downtime, fewer tech headaches, and access to expert help without needing an in-house team.`,
          },
          {
            q: "How does SimpleTech's process work?",
            a: `We start with a free consultation right here in Montclair to assess your current setup. From there, we build a custom ${nameLower} plan that fits your business. Our local team monitors, responds quickly, and keeps things secure and efficient.`,
          },
          {
            q: "Which areas do you serve?",
            a: "We proudly serve businesses in Montclair, Upland, Ontario, Claremont, Rancho Cucamonga, and surrounding areas. Because we're based right here in Montclair, we can provide fast on-site service when needed.",
          },
          {
            q: "What makes you different from other IT companies?",
            a: "Unlike national providers, we're a locally owned Montclair IT company that offers personalized support. You'll speak with real people, not call centers, and get rapid response times from experts who understand your specific business environment.",
          },
          {
            q: "How do I schedule a consultation?",
            a: "It's easy—just call us at\n(909) 255-9112 or email contact@simpletechhelper.com. You can also stop by our office at 8880 N Benson Ave #121, Montclair, CA 91763.",
          },
        ];

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SiteHeader active="services" />
      <main id="main-content" className="flex-1">
        {/* Hero */}
        <section
          className="relative bg-[#0b0a3d] bg-cover bg-center text-white overflow-hidden min-h-[520px] md:min-h-[600px] flex items-center"
          style={{ backgroundImage: `url(${serviceBg})` }}
        >
          <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
            <div className="lg:w-1/2 max-w-xl">
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
                {service.title}
              </h1>
              <p className="mt-3 text-xl md:text-2xl font-bold text-sky-400">
                {service.heroSubtitle ?? "Reduce Downtime and Improve Security"}
              </p>
              <p className="mt-8 text-base md:text-lg text-white/85 leading-relaxed">
                {service.description}
              </p>
              <div className="mt-10">
                <Link
                  to="/contact"
                  className="inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
                >
                  {service.heroCtaLabel ?? "SCHEDULE A CONSULTATION"}
                </Link>
                {service.slug === "tech-support-for-seniors" && (
                  <div className="mt-5 flex items-center gap-2 text-sm text-white/90">
                    <span className="text-yellow-400 tracking-tight" aria-hidden>★★★★★</span>
                    <span><strong>5.0 stars</strong> on Google · 30+ local reviews</span>
                  </div>
                )}
              </div>
              <p className="mt-6 text-sm text-white/70 max-w-sm leading-relaxed">
                {service.heroBestFor ?? "Best for small offices, multi-site teams, compliance-minded businesses."}
              </p>
            </div>
          </div>

          <div
            className={`hidden lg:block absolute z-0 pointer-events-none ${
              service.heroImagePosition ?? "right-0 bottom-0"
            }`}
          >
            <img
              src={service.heroImage ?? HERO_IMAGES[service.slug] ?? managedItHero}
              alt={
                service.slug === "website-design"
                  ? "Small business website design example on laptop and mobile"
                  : service.slug === "tech-support-for-seniors"
                  ? "Patient in-home tech help for seniors"
                  : `SimpleTech team providing ${nameLower}`
              }
              className={`w-auto object-contain drop-shadow-2xl ${
                service.heroImageClass ?? "max-h-[520px]"
              }`}
            />
          </div>
        </section>

        {/* White-bands background wrapper (shared) */}
        <div className="bg-white bg-cover bg-top" style={{ backgroundImage: `url(${whiteBandsBg})` }}>
          {/* Why Choose Us */}
          <section className="relative py-20 md:py-28">
            <div className="relative max-w-6xl mx-auto px-6 text-center">
              <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
                {service.slug === "website-design"
                  ? "Why Choose Our Small Business Website Design Services?"
                  : service.slug === "software-integration"
                  ? "Why Choose SimpleTech for Software Integration?"
                  : service.slug === "tech-support-for-seniors"
                  ? "Why Families Trust SimpleTech with Senior Tech Help"
                  : service.slug === "ada-compliant-website"
                  ? "Why Businesses Choose SimpleTech for ADA Compliance"
                  : service.slug === "cyber-security"
                  ? "Why Small Businesses Choose SimpleTech for Cybersecurity"
                  : `Why Choose Our ${name} Services?`}
              </h2>
              <p className="mt-5 text-slate-600 max-w-2xl mx-auto leading-relaxed">
                {service.slug === "website-design"
                  ? "We're your hands-on web design partner. We build sites you own, designed to rank — backed by real people who answer when you call."
                  : service.slug === "software-integration"
                  ? "We're not just plugging apps together — we're rebuilding how work flows through your business, with the lowest-cost tool stack that gets the job done."
                  : service.slug === "tech-support-for-seniors"
                  ? "We're not a call center. We're a small, local team that treats every senior client with the kindness we'd want for our own parents and grandparents."
                  : service.slug === "ada-compliant-website"
                  ? "Overlay widgets won't protect you from an ADA lawsuit. We do the real work — code-level fixes, manual screen-reader testing, and a written conformance statement your attorney can stand behind."
                  : service.slug === "cyber-security"
                  ? "Enterprise-grade tools, configured and managed by a small local team that actually picks up the phone."
                  : "SimpleTech is your trusted local partner. You can rely on us for fast response times and hands-on support."}
              </p>

              <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12 text-left">
                {(service.slug === "website-design"
                  ? [
                      { Icon: Search, title: "Built to Rank on Google", body: "On-page SEO, schema markup, and fast load times baked in from day one — not bolted on later." },
                      { Icon: Smartphone, title: "Mobile-First Design", body: "Over 60% of your visitors are on a phone. Every site we build looks great and converts on mobile." },
                      { Icon: UserRound, title: "Real People, Real Support", body: "Talk directly with owner Armands and our team — no offshore queues, no ticket numbers." },
                      { Icon: KeyRound, title: "You Own Everything", body: "You keep the domain, hosting login, and source files. No lock-in, no held-hostage hosting fees." },
                      { Icon: Accessibility, title: "ADA & Accessibility Ready", body: "WCAG 2.1 best practices reduce legal risk and open your site to more customers.", to: "/services/$slug" as const, params: { slug: "ada-compliant-website" } },
                      { Icon: Wrench, title: "Ongoing Maintenance Plans", body: "Updates, backups, security patches, and monthly tweaks so your site never goes stale.", to: "/services/managed-it-services" as const },
                      { Icon: ShieldCheck, title: "Hardened Site Security", body: "SSL, firewall, malware scanning, and brute-force protection so your site stays online and trusted.", to: "/services/$slug" as const, params: { slug: "cyber-security" } },
                    ]
                  : service.slug === "software-integration"
                  ? [
                      { Icon: Hexagon, title: "Platform-Neutral", body: "We build on Zapier, Make, Power Automate, n8n, or native APIs — whichever fits your stack and budget best." },
                      { Icon: Clock, title: "Hours Back Every Week", body: "Most clients reclaim 5–20 hours per week of manual data entry inside the first 60 days." },
                      { Icon: CheckCircle2, title: "Fix Data Errors at the Source", body: "One record, one source of truth. No more typos, duplicates, or \"which spreadsheet is right?\"" },
                      { Icon: FolderOpen, title: "Real Documentation", body: "Every workflow is documented in plain English so your team knows what runs, when, and why." },
                      { Icon: KeyRound, title: "You Own the Automations", body: "Your accounts, your access, your zaps. You can take everything with you any time." },
                      { Icon: Wrench, title: "Ongoing Optimization", body: "Tools and processes change. We monitor, fix, and improve your workflows so they keep working." },
                    ]
                  : service.slug === "tech-support-for-seniors"
                  ? [
                      { Icon: Heart, title: "Patient & Never Rushed", body: "We work at your pace. If you need something explained three times, we explain it three times — no sighing, no judgment." },
                      { Icon: MessageCircle, title: "Plain-English Only", body: "No tech jargon, no acronyms. We explain things the same way we would to a friend who isn't a 'computer person.'" },
                      { Icon: Home, title: "In-Home or Remote Help", body: "We can come to you, walk you through it on the phone, or fix things remotely — whichever you're most comfortable with." },
                      { Icon: ShieldAlert, title: "Scam & Safety Help", body: "We help spot suspicious calls, emails, and texts — and we never use scare tactics ourselves." },
                      { Icon: UserRound, title: "Trusted & Local", body: "Owner Armands and a small local team. Same person every visit when possible. References available." },
                      { Icon: DollarSign, title: "Fair, Transparent Pricing", body: "Flat rates by the hour or visit. No surprise charges. We tell you the price before we start." },
                    ]
                  : service.slug === "ada-compliant-website"
                  ? [
                      { Icon: ShieldCheck, title: "Real WCAG 2.1 AA Audits", body: "We test against every Level AA success criterion — automated scans plus manual keyboard and screen-reader testing. No checkbox theater." },
                      { Icon: ShieldAlert, title: "No Overlay Widgets", body: "Overlays are now named in hundreds of ADA lawsuits. We fix accessibility at the source: your HTML, CSS, components, and content." },
                      { Icon: FolderOpen, title: "Demand-Letter Response", body: "Received a complaint? We can start within 48 hours, coordinate with your attorney, and produce the documentation legal counsel needs." },
                      { Icon: CheckCircle2, title: "Written Conformance Statement", body: "On completion you receive a dated WCAG 2.1 AA conformance statement and full remediation report — proof of good-faith compliance." },
                      { Icon: Wrench, title: "Code-Level Fixes", body: "Semantic HTML, ARIA when it's needed (and only when it's needed), accessible forms, focus management, contrast — fixed properly, not patched over." },
                      { Icon: Clock, title: "Ongoing Monitoring", body: "New pages, images, and PDFs can break compliance overnight. Our monitoring plans catch regressions before a complainant does." },
                    ]
                  : service.slug === "cyber-security"
                  ? [
                      { Icon: Headphones, title: "24/7 MDR Monitoring", body: "Managed Detection & Response — real humans watching for attacks around the clock, not just an alert nobody reads." },
                      { Icon: ShieldCheck, title: "Cyber-Insurance Ready", body: "We configure the exact controls (MFA, EDR, backups, training) your insurer requires at renewal — so you stay covered." },
                      { Icon: CheckCircle2, title: "Compliance Mapping", body: "HIPAA, PCI, SEC, FINRA, CCPA — we map our controls to your framework and provide audit evidence." },
                      { Icon: Fingerprint, title: "Layered Protection", body: "No single tool stops everything. We deploy email, endpoint, network, identity, and backup controls that work together." },
                      { Icon: Users, title: "Human Training", body: "Quarterly phishing simulations and short, friendly training videos that your team will actually watch." },
                      { Icon: ShieldAlert, title: "Incident Response Plan", body: "If something does happen, you have a written response plan, off-site backups, and a partner who answers the call." },
                    ]
                  : [
                      { Icon: Clock, title: "24/7 Proactive Monitoring", body: "Stop problems before they disrupt your business." },
                      { Icon: ShieldCheck, title: "Secure, Compliant Systems", body: "We safeguard your data with advanced cybersecurity." },
                      { Icon: UserRound, title: "Personalized Local Service", body: "Owner Armands & our Montclair team treat your business like it's our own." },
                      { Icon: HandCoins, title: "Cost-Effective Support Plans", body: "Predictable pricing helps you control your budget." },
                      { Icon: Fingerprint, title: "Scalable Solutions", body: "From 5 to 500 users, we tailor support to your growth." },
                      { Icon: Headphones, title: "Rapid Response Guarantee", body: "Get expert help within minutes when issues arise." },
                    ]
                ).map((item) => {
                  const { Icon, title, body } = item;
                  const inner = (
                    <>
                      <Icon className="w-10 h-10 text-[#0b0a3d] shrink-0 group-hover:text-sky-500 transition-colors" strokeWidth={1.5} />
                      <div>
                        <h3 className="text-base font-bold text-[#0b0a3d] group-hover:text-sky-600 transition-colors">
                          {title}
                          {"to" in item && item.to ? <ArrowRight className="inline-block w-4 h-4 ml-1 -mt-0.5 opacity-60" /> : null}
                        </h3>
                        <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
                      </div>
                    </>
                  );
                  if ("to" in item && item.to) {
                    if ("params" in item && item.params) {
                      return (
                        <Link key={title} to={item.to} params={item.params} className="group flex gap-5">
                          {inner}
                        </Link>
                      );
                    }
                    return (
                      <Link key={title} to={item.to} className="group flex gap-5">
                        {inner}
                      </Link>
                    );
                  }
                  return (
                    <div key={title} className="group flex gap-5">
                      {inner}
                    </div>
                  );
                })}
              </div>

              <div className="mt-16">
                <Link
                  to="/contact"
                  className="inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
                >
                  FREE CONSULTATION
                </Link>
              </div>
            </div>
          </section>

          {service.slug === "tech-support" && <CommonIssuesSection />}
          {service.slug === "voip" && <VoipExtrasSection />}
          {service.slug === "software-integration" && <IntegrationToolsSection />}
          {service.slug === "tech-support-for-seniors" && <SeniorsExtrasSection />}
          {service.slug === "cyber-security" && <CyberExtrasSection />}

          {/* Reviews */}
          <section className="relative py-20 md:py-28">
            <div className="relative max-w-6xl mx-auto px-6">
              <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight text-center">
                Expert {name} Reviews
              </h2>

              <div className="mt-12 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="flex items-center gap-8 px-8 pt-6 border-b border-slate-100">
                  <button className="pb-4 text-sm font-semibold text-[#0b0a3d] border-b-2 border-[#0b0a3d] -mb-px">
                    All reviews <span className="text-slate-500 font-normal">5.0</span>
                  </button>
                  <button className="pb-4 text-sm font-semibold text-slate-600 flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 to-red-500 text-white text-[10px] font-bold flex items-center justify-center">G</span>
                    Google <span className="text-slate-500 font-normal">5.0</span>
                  </button>
                  <button className="pb-4 text-sm font-semibold text-slate-600 flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full bg-[#1877F2] text-white text-[10px] font-bold flex items-center justify-center">f</span>
                    Facebook <span className="text-slate-500 font-normal">5.0</span>
                  </button>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 px-8 py-5 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <span className="text-base font-bold text-[#0b0a3d]">Excellent</span>
                    <div className="flex text-amber-400 text-lg leading-none">★★★★★</div>
                    <span className="text-sm font-semibold text-[#0b0a3d]">5.0</span>
                    <span className="text-sm text-slate-500">| 32 reviews</span>
                  </div>
                  <button className="text-sm font-semibold text-[#0b0a3d] border border-slate-300 rounded-md px-4 py-2 hover:bg-slate-50 transition-colors">
                    Write a review
                  </button>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 p-6">
                  {[
                    { initial: "M", name: "Malcolm Brown", date: "31 December 2025", body: "Armands was an incredible resource to have. He obviously knows what he's talking about." },
                    { initial: "L", name: "Legal Learning", date: "31 December 2025", body: "I've used Armands many times. For my computer and my phone. He can do anything tech related…" },
                    { initial: "Y", name: "Yoochul Chong", date: "31 December 2025", body: "I was interested in starting a NAS, Armands was amazing in helping with that from advice to product choice…" },
                    { initial: "V", name: "Van Castaneda", date: "7 November 2025", body: "Today i found out that my corporate website is not ADA compliant. That's a huge risk so I called my…" },
                  ].map(({ initial, name: rn, date, body }) => (
                    <div key={rn} className="rounded-xl border border-slate-200 p-5 bg-white">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-sky-100 text-[#0b0a3d] font-bold flex items-center justify-center">
                          {initial}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-bold text-[#0b0a3d] leading-tight">{rn}</p>
                          <p className="text-xs text-slate-500">{date}</p>
                        </div>
                        <span className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-red-500 text-white text-[10px] font-bold flex items-center justify-center">G</span>
                      </div>
                      <div className="mt-3 flex text-amber-400 text-sm leading-none">★★★★★</div>
                      <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-4">{body}</p>
                      <button className="mt-3 text-xs font-semibold text-slate-500 hover:text-[#0b0a3d]">
                        Read more
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* How Onboarding Works */}
        <section
          className="relative bg-[#0b0a3d] bg-cover bg-center text-white"
          style={{ backgroundImage: `url(${onboardingBg})` }}
        >
          <div className="relative max-w-7xl mx-auto px-6 pt-20 md:pt-28 pb-0 grid lg:grid-cols-2 gap-10 items-end">
            <div className="hidden lg:flex justify-start self-end -mt-[300px] relative z-20">
              <img
                src={armandsHeadshot}
                alt={`Armands Williams, SimpleTech owner and lead technician for ${service.title.toLowerCase()} in Southern California`}
                loading="lazy"
                className="max-h-[760px] w-auto object-contain block"
              />

            </div>
            <div className="pb-20 md:pb-28">
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight">
                {service.slug === "website-design"
                  ? "Our 3-Step Web Design & Development Process"
                  : service.slug === "software-integration"
                  ? "How Our Software Integration Process Works"
                  : service.slug === "tech-support-for-seniors"
                  ? "How a SimpleTech Senior Visit Works"
                  : service.slug === "ada-compliant-website"
                  ? "How Our ADA Audit & Remediation Process Works"
                  : service.slug === "cyber-security"
                  ? "How a SimpleTech Cybersecurity Engagement Works"
                  : "How Onboarding Works"}
              </h2>
              <p className="mt-4 text-white/80 text-lg">
                {service.slug === "website-design"
                  ? "A predictable, transparent process. You'll know exactly what's happening at every step."
                  : service.slug === "software-integration"
                  ? "A repeatable, transparent process that ends with workflows that actually run — not a half-built project."
                  : service.slug === "tech-support-for-seniors"
                  ? "No mystery. No pushy sales. Just a friendly, predictable process."
                  : service.slug === "ada-compliant-website"
                  ? "A clear, four-step path from your first call to a written WCAG 2.1 AA conformance statement."
                  : service.slug === "cyber-security"
                  ? "Clear, predictable, and proven. You'll know your risks, your gaps, and your plan before any work begins."
                  : "A predictable process you can trust. We do our best to keep it simple and clear."}
              </p>
              <div className="mt-8">
                <Link
                  to="/contact"
                  className="inline-block border-2 border-sky-400 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md hover:bg-sky-400 transition-colors"
                >
                  SCHEDULE CONSULTATION
                </Link>
              </div>

              <div className="mt-12 grid sm:grid-cols-3 gap-5">
                {(service.slug === "website-design"
                  ? [
                      { step: "1", title: "DISCOVERY", body: "We learn your business, customers, goals, and competitors. You tell us what's broken about your current site." },
                      { step: "2", title: "DESIGN & COPY", body: "We draft pages, write SEO-ready copy, and design a mobile-first layout you review before any code is written." },
                      { step: "3", title: "BUILD & LAUNCH", body: "We develop, test on every device, set up hosting and analytics, then launch — with a final walkthrough so you can update it yourself." },
                    ]
                  : service.slug === "software-integration"
                  ? [
                      { step: "1", title: "DISCOVERY & AUDIT", body: "We map your current tools, identify the manual tasks costing you the most time, and prioritize the highest-ROI automations." },
                      { step: "2", title: "DESIGN & BUILD", body: "We design the workflows, pick the right platform (Zapier, Make, Power Automate, or custom), and build them in your accounts." },
                      { step: "3", title: "TEST, TRAIN & DOCUMENT", body: "We test every edge case, train your team on how it works, and hand over plain-English documentation so nothing is a black box." },
                    ]
                  : service.slug === "tech-support-for-seniors"
                  ? [
                      { step: "1", title: "WE CHAT FIRST", body: "Free 15-minute phone call to understand what's going on and what you'd like help with. No technical questions — just a friendly conversation." },
                      { step: "2", title: "WE SCHEDULE & QUOTE", body: "We schedule the visit (in-home or remote) at a time that works for you and tell you the price up front. No surprises." },
                      { step: "3", title: "WE HELP & TEACH", body: "We fix the issue, then take the time to show you how to do it yourself next time — if you want. You set the pace." },
                    ]
                  : service.slug === "ada-compliant-website"
                  ? [
                      { step: "1", title: "DISCOVERY CALL", body: "Free 20-minute call to review your site, your industry's risk level, and whether you've already received a demand letter. Fixed quote within 24 hours." },
                      { step: "2", title: "FULL WCAG 2.1 AA AUDIT", body: "Automated scans (axe, WAVE, Lighthouse) plus manual keyboard, screen-reader (NVDA/VoiceOver/JAWS), and contrast testing. Delivered as a prioritized report." },
                      { step: "3", title: "CODE-LEVEL REMEDIATION", body: "We fix issues at the source — semantic HTML, ARIA, focus management, contrast, forms — and re-test until your site passes WCAG 2.1 AA." },
                    ]
                  : service.slug === "cyber-security"
                  ? [
                      { step: "1", title: "RISK ASSESSMENT", body: "We audit your accounts, devices, network, backups, and policies — then give you a written report showing what's protected and what's not." },
                      { step: "2", title: "PRIORITIZED ROADMAP", body: "We prioritize the highest-impact, lowest-cost wins first (MFA, backups, email filtering) and phase larger projects across the next 6–12 months." },
                      { step: "3", title: "DEPLOY & MONITOR", body: "We configure each control, train your team, and monitor 24/7. You get quarterly reviews and a clear dashboard of where you stand." },
                    ]
                  : [
                      { step: "1", title: "DISCOVERY", body: "Review your team size, devices, applications, current issues, and priorities." },
                      { step: "2", title: "ASSESSMENT", body: "Inventory + risk review (accounts, devices, backups, network baseline)." },
                      { step: "3", title: "PLANNING", body: "Right-sized support plan with prioritized improvements and a phased rollout." },
                    ]
                ).map(({ step, title, body }) => (
                  <div key={step} className="rounded-xl border border-white/15 bg-white/5 backdrop-blur-sm p-5">
                    <p className="text-xs font-bold text-sky-400 tracking-widest">{step} {title}</p>
                    <p className="mt-3 text-sm text-white/85 leading-relaxed">{body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Service Packages */}
        {service.slug !== "voip" && service.slug !== "software-integration" && (
        <section className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
                {service.slug === "website-design"
                  ? "Affordable Small Business Website Design Packages"
                  : service.slug === "software-integration"
                  ? "Software Integration Packages"
                  : service.slug === "tech-support-for-seniors"
                  ? "Senior Tech Help Plans"
                  : service.slug === "ada-compliant-website"
                  ? "ADA Compliance Packages"
                  : service.slug === "cyber-security"
                  ? "Cybersecurity Packages"
                  : `${name} Packages`}
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">
                {service.slug === "website-design"
                  ? "Structured packages with clear deliverables and predictable pricing. Need something custom? We tailor every package around your goals, content, and budget."
                  : service.slug === "software-integration"
                  ? "Structured packages for common situations. We also build fully custom automations when your processes don't fit a template."
                  : service.slug === "tech-support-for-seniors"
                  ? "Pay only for what you need. Most clients start with a one-time visit, then switch to a monthly plan if they want ongoing peace of mind."
                  : service.slug === "ada-compliant-website"
                  ? "Fixed-price packages for the most common situations — from a one-time audit to ongoing monitoring. Already received a demand letter? Ask about our rapid-response engagement."
                  : service.slug === "cyber-security"
                  ? "Three tiers built to match common business needs and cyber-insurance requirements. Custom packages available for regulated industries."
                  : `We offer structured ${nameLower} packages for clear coverage and predictable support. We can also tailor a custom plan when your environment, compliance needs, or growth goals require something more specific.`}
              </p>
            </div>

            <div className="mt-14 grid md:grid-cols-3 gap-6">
              {(service.slug === "website-design"
                ? [
                    {
                      title: "Starter",
                      intro: "Best for solo operators, contractors, and new businesses that need a clean online presence (1–5 pages).",
                      features: [
                        "Custom mobile-first design (up to 5 pages)",
                        "SEO-ready copywriting",
                        "Contact form & Google Maps integration",
                        "Google Analytics & Search Console setup",
                        "One round of revisions",
                      ],
                    },
                    {
                      title: "Growth",
                      intro: "Best for established small businesses that want to rank locally and capture more leads (6–12 pages + Local SEO).",
                      features: [
                        "Everything in Starter",
                        "Service-page and city-page structure for local SEO",
                        "FAQ schema markup on key pages",
                        "Google Business Profile optimization",
                        "Lead capture forms & basic CRM integration",
                        "Two rounds of revisions",
                      ],
                    },
                    {
                      title: "Full Service",
                      intro: "Best for multi-location businesses, e-commerce, or compliance-minded industries (legal, medical, financial).",
                      features: [
                        "Everything in Growth",
                        "Custom integrations (CRM, booking, payments, e-commerce)",
                        "ADA / WCAG 2.1 accessibility audit & remediation",
                        "Hosting, security, & uptime monitoring",
                        "Monthly maintenance, updates, & content edits",
                        "Quarterly SEO performance reviews",
                      ],
                    },
                  ]
                : service.slug === "software-integration"
                ? [
                    {
                      title: "Starter\nSingle Workflow",
                      intro: "Best for businesses with one specific manual task they want to automate.",
                      features: [
                        "Discovery call & workflow design",
                        "One automation built in Zapier, Make, or Power Automate",
                        "Up to 3 connected apps",
                        "Testing and team walkthrough",
                        "30 days of post-launch support",
                      ],
                    },
                    {
                      title: "Growth\nMulti-Workflow",
                      intro: "Best for established small businesses ready to overhaul multiple processes.",
                      features: [
                        "Workflow audit across your entire app stack",
                        "Up to 5 automations built and connected",
                        "CRM, accounting, and email/marketing integrations",
                        "Centralized documentation in your workspace",
                        "90 days of post-launch support and tweaks",
                      ],
                    },
                    {
                      title: "Fully Managed\nOngoing Partner",
                      intro: "Best for growing teams that want a long-term partner managing their automation stack.",
                      features: [
                        "Everything in Growth",
                        "Monthly workflow health monitoring & error resolution",
                        "Unlimited minor edits and additions",
                        "New workflow builds at a discounted block rate",
                        "Quarterly review meetings to find new opportunities",
                      ],
                    },
                  ]
                : service.slug === "tech-support-for-seniors"
                ? [
                    {
                      title: "Single Visit\nPay As You Go",
                      intro: "Best for one-off help: a new device, a specific problem, or a tutoring session.",
                      features: [
                        "Flat hourly rate, told to you up front",
                        "In-home visit or remote help",
                        "Up to 2 hours of patient, focused help",
                        "Plain-language notes left behind so you can do it yourself next time",
                      ],
                    },
                    {
                      title: "Monthly\nCheck-In",
                      intro: "Best for seniors who want regular tech help and a familiar face.",
                      features: [
                        "One scheduled visit or call each month",
                        "Free unlimited 'quick questions' by phone or text between visits",
                        "Software updates, safety check, scam-call review",
                        "Same helper each month whenever possible",
                      ],
                    },
                    {
                      title: "Family Plan\nFor Adult Children",
                      intro: "Best for adult children who want a trusted helper for a parent or grandparent.",
                      features: [
                        "Everything in Monthly Check-In",
                        "You're cc'd on visit summaries (with parent's permission)",
                        "Priority response when scams or urgent issues come up",
                        "You pay; we handle the rest — no more being the family IT person",
                      ],
                    },
                  ]
                : service.slug === "ada-compliant-website"
                ? [
                    {
                      title: "Audit\nOne-Time Report",
                      intro: "Best for small sites that want to know where they stand and get a prioritized fix list.",
                      features: [
                        "Full WCAG 2.1 Level AA audit",
                        "Automated + manual screen-reader testing",
                        "Prioritized report with code-level recommendations",
                        "60-minute walkthrough call with your team or developer",
                        "Fixed price, delivered in 5–10 business days",
                      ],
                    },
                    {
                      title: "Audit + Remediation\nCompliance Project",
                      intro: "Best for businesses that want their site fully fixed and a written conformance statement.",
                      features: [
                        "Everything in the Audit package",
                        "Code-level remediation by accessibility-trained developers",
                        "Re-test cycle until your site passes WCAG 2.1 AA",
                        "Written WCAG 2.1 AA conformance statement",
                        "Accessibility statement page added to your site",
                      ],
                    },
                    {
                      title: "Compliance Care\nOngoing Monitoring",
                      intro: "Best for active sites that publish new pages, blog posts, or product listings.",
                      features: [
                        "Everything in Audit + Remediation",
                        "Monthly automated scans + quarterly manual review",
                        "Block of remediation hours included each month",
                        "Annual updated conformance statement",
                        "Priority response if you receive a demand letter",
                      ],
                    },
                  ]
                : service.slug === "cyber-security"
                ? [
                    {
                      title: "Essentials\nThe Baseline",
                      intro: "Best for small businesses establishing their first real security program.",
                      features: [
                        "Multi-factor authentication (MFA) deployment",
                        "Endpoint protection (next-gen antivirus / EDR)",
                        "Email filtering & anti-phishing",
                        "Encrypted, off-site backups with restore testing",
                        "Annual security awareness training",
                        "Written cybersecurity policy & incident response plan",
                      ],
                    },
                    {
                      title: "Plus\nInsurance & Audit Ready",
                      intro: "Best for businesses with cyber insurance, vendor security questionnaires, or audit requirements.",
                      features: [
                        "Everything in Essentials",
                        "24/7 Managed Detection & Response (MDR)",
                        "Dark-web monitoring & password manager rollout",
                        "Quarterly phishing simulations & training",
                        "Conditional access & identity policies (Microsoft 365 / Google Workspace)",
                        "Cyber-insurance attestation support at renewal",
                      ],
                    },
                    {
                      title: "Fully Managed\nCompliance-Grade",
                      intro: "Best for healthcare, legal, financial, and regulated firms.",
                      features: [
                        "Everything in Plus",
                        "Compliance mapping (HIPAA, PCI, SEC, FINRA, CCPA)",
                        "Vulnerability scanning & remediation",
                        "BAAs and vendor risk management",
                        "Quarterly executive risk reports",
                        "Incident response retainer & tabletop exercises",
                      ],
                    },
                  ]
                : [
                    {
                      title: "Essential",
                      intro: "Best for small teams that need dependable support and basic coverage.",
                      features: ["Help desk support", "Monitoring & patching cadence", "Onboarding + documentation"],
                    },
                    {
                      title: "Plus",
                      intro: "Best for teams that need stronger security and operational consistency.",
                      features: ["Everything in Essentials", "MFA + access policy guidance", "Backup strategy support"],
                    },
                    {
                      title: "Fully Managed",
                      intro: "Best for compliance-conscious teams and multi-site operations.",
                      features: ["Everything in Plus", "Security hardening roadmap", "Quarterly optimization reviews"],
                    },
                  ]
              ).map(({ title, intro, features }) => (
                <div
                  key={title}
                  className="relative rounded-2xl overflow-hidden bg-[#0b0a3d] bg-cover bg-center p-10 text-white shadow-xl"
                  style={{ backgroundImage: `url(${onboardingBg})` }}
                >
                  <h3 className="text-3xl font-extrabold text-sky-400 whitespace-pre-line leading-tight">
                    {title}
                  </h3>
                  <p className="mt-6 text-sm font-bold text-white leading-relaxed">{intro}</p>
                  <ul className="mt-8 space-y-3 text-sm text-white">
                    {features.map((f) => (
                      <li key={f} className="flex gap-3">
                        <span className="text-sky-400 mt-1">•</span>
                        <span className="font-semibold">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
        )}

        {/* FAQs */}
        <section className="bg-white py-20 md:py-28">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight text-center">
              {name} FAQs
            </h2>

            <div className="mt-14 space-y-4">
              {faqs.map(({ q, a }, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="font-bold text-[#0b0a3d]">{q}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-slate-500 shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`}
                    />
                  </button>
                  <div
                    className={`px-6 text-slate-600 text-sm leading-relaxed transition-all duration-300 ease-in-out ${openFaq === i ? "pb-5 max-h-80 opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}
                  >
                    {a}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Other Services We Offer */}
        <section className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight text-center">
              Other Services We Offer
            </h2>
            <p className="mt-6 text-slate-600 max-w-3xl mx-auto text-center leading-relaxed">
              SimpleTech is more than just one service — we help businesses stay connected,
              secure, and efficient. Whether it's remote assistance, software integration, or building
              a smarter tech plan, we're just a call away. When on-site help is needed, being local
              means we respond fast.
            </p>

            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {otherServices.map(({ icon: Icon, slug, shortTitle, excerpt }) => (
                <Link
                  key={slug}
                  {...(slug === "managed-it"
                    ? { to: "/services/managed-it-services" as const }
                    : { to: "/services/$slug" as const, params: { slug } })}
                  className="group bg-white hover:-translate-y-1 transition-all rounded-2xl p-8 border border-slate-100 shadow-md hover:shadow-2xl flex flex-col"
                >
                  <Icon className="w-10 h-10 text-indigo-600 mb-5" strokeWidth={1.5} />
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{shortTitle}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed flex-1">{excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold tracking-wider text-sky-500 group-hover:text-sky-600">
                    LEARN MORE <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>

            <div className="mt-16 max-w-3xl mx-auto rounded-2xl border border-slate-200 bg-slate-50 p-10 text-center">
              <p className="text-lg text-slate-700 italic leading-relaxed">
                "I had an excellent experience working with Armand Williams for my IT needs.
                His expertise, professionalism, and dedication to solving IT challenges
                make him a valuable resource."
              </p>
              <div className="mt-6 flex items-center justify-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#0b0a3d] text-white font-bold flex items-center justify-center text-sm">
                  JK
                </div>
                <div className="text-left">
                  <p className="font-bold text-[#0b0a3d] text-sm">Jeff K</p>
                  <p className="text-xs text-slate-500">Ontario, CA</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Service Area */}
        <section className="bg-white py-20 md:py-28">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center">
              <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
                Our Service Area
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">
                We provide {nameLower} across the Inland Empire and Orange County.
                Choose your city to learn more.
              </p>
            </div>

            <div className="mt-14 grid sm:grid-cols-2 md:grid-cols-3 gap-x-12 gap-y-10">
              {cityList.map((c) => {
                // Only Managed IT has per-city location pages right now.
                // For every other service, render the city name as plain text
                // so users aren't sent to an unrelated service's location page.
                const hasManagedItPage = service.slug === "managed-it" && c.slug in locationCities;
                const hasTechSupportMontclair = service.slug === "tech-support" && c.slug === "montclair";
                const hasVoipMontclair = service.slug === "voip" && c.slug === "montclair";
                const hasWebDesignMontclair = service.slug === "website-design" && c.slug === "montclair";
                const hasSoftwareIntegrationMontclair = service.slug === "software-integration" && c.slug === "montclair";
                const hasSeniorTechMontclair = service.slug === "tech-support-for-seniors" && c.slug === "montclair";

                if (hasManagedItPage) {
                  return (
                    <Link
                      key={c.slug}
                      to="/services/managed-it-services/$city"
                      params={{ city: c.slug }}
                      className="flex items-center gap-4 group"
                    >
                      <MapPin className="w-8 h-8 text-indigo-600 shrink-0 group-hover:text-sky-500 transition-colors" strokeWidth={2.2} fill="currentColor" stroke="white" />
                      <span className="text-base font-bold text-slate-700 group-hover:text-[#0b0a3d] group-hover:underline">{c.name}</span>
                    </Link>
                  );
                }

                if (service.slug === "managed-it" && c.slug === "claremont") {
                  return (
                    <Link
                      key={c.slug}
                      to="/managed-it-services-claremont-ca"
                      className="flex items-center gap-4 group"
                    >
                      <MapPin className="w-8 h-8 text-indigo-600 shrink-0 group-hover:text-sky-500 transition-colors" strokeWidth={2.2} fill="currentColor" stroke="white" />
                      <span className="text-base font-bold text-slate-700 group-hover:text-[#0b0a3d] group-hover:underline">{c.name}</span>
                    </Link>
                  );
                }

                if (hasTechSupportMontclair) {
                  return (
                    <Link
                      key={c.slug}
                      to="/tech-support-montclair-ca"
                      className="flex items-center gap-4 group"
                    >
                      <MapPin className="w-8 h-8 text-indigo-600 shrink-0 group-hover:text-sky-500 transition-colors" strokeWidth={2.2} fill="currentColor" stroke="white" />
                      <span className="text-base font-bold text-slate-700 group-hover:text-[#0b0a3d] group-hover:underline">{c.name}</span>
                    </Link>
                  );
                }

                if (hasVoipMontclair) {
                  return (
                    <Link
                      key={c.slug}
                      to="/voip-montclair-ca"
                      className="flex items-center gap-4 group"
                    >
                      <MapPin className="w-8 h-8 text-indigo-600 shrink-0 group-hover:text-sky-500 transition-colors" strokeWidth={2.2} fill="currentColor" stroke="white" />
                      <span className="text-base font-bold text-slate-700 group-hover:text-[#0b0a3d] group-hover:underline">{c.name}</span>
                    </Link>
                  );
                }

                if (hasWebDesignMontclair) {
                  return (
                    <Link
                      key={c.slug}
                      to="/web-design-montclair-ca"
                      className="flex items-center gap-4 group"
                    >
                      <MapPin className="w-8 h-8 text-indigo-600 shrink-0 group-hover:text-sky-500 transition-colors" strokeWidth={2.2} fill="currentColor" stroke="white" />
                      <span className="text-base font-bold text-slate-700 group-hover:text-[#0b0a3d] group-hover:underline">{c.name}</span>
                    </Link>
                  );
                }

                if (hasSoftwareIntegrationMontclair) {
                  return (
                    <Link
                      key={c.slug}
                      to="/software-integration-montclair-ca"
                      className="flex items-center gap-4 group"
                    >
                      <MapPin className="w-8 h-8 text-indigo-600 shrink-0 group-hover:text-sky-500 transition-colors" strokeWidth={2.2} fill="currentColor" stroke="white" />
                      <span className="text-base font-bold text-slate-700 group-hover:text-[#0b0a3d] group-hover:underline">{c.name}</span>
                    </Link>
                  );
                }

                if (hasSeniorTechMontclair) {
                  return (
                    <Link
                      key={c.slug}
                      to="/senior-tech-support-montclair-ca"
                      className="flex items-center gap-4 group"
                    >
                      <MapPin className="w-8 h-8 text-indigo-600 shrink-0 group-hover:text-sky-500 transition-colors" strokeWidth={2.2} fill="currentColor" stroke="white" />
                      <span className="text-base font-bold text-slate-700 group-hover:text-[#0b0a3d] group-hover:underline">{c.name}</span>
                    </Link>
                  );
                }






                return (
                  <div key={c.slug} className="flex items-center gap-4">
                    <MapPin className="w-8 h-8 text-indigo-600 shrink-0" strokeWidth={2.2} fill="currentColor" stroke="white" />
                    <span className="text-base font-bold text-slate-700">{c.name}</span>
                  </div>
                );
              })}
              <div className="flex items-center gap-4">
                <MapPin className="w-8 h-8 text-indigo-600 shrink-0" strokeWidth={2.2} fill="currentColor" stroke="white" />
                <span className="text-base font-bold text-slate-700">Other surrounding communities.</span>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section
          className="relative bg-[#0b0a3d] bg-cover bg-center text-white py-20 md:py-28"
          style={{ backgroundImage: `url(${onboardingBg})` }}
        >
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">
              Ready for {name}?
            </h2>
            <p className="mt-4 text-lg text-white/80">Let's work together.</p>
            <div className="mt-10">
              <Link
                to="/contact"
                className="inline-block border-2 border-sky-400 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md hover:bg-sky-400 transition-colors"
              >
                SCHEDULE A CONSULTATION
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

const ISSUES_LEFT = [
  { num: "01", Icon: Mail, title: "Email & login access", desc: "Email access, password resets, MFA and login issues.", color: "bg-teal-400" },
  { num: "02", Icon: CheckCircle2, title: "Performance troubleshooting", desc: "Slow computers, crashing apps, performance troubleshooting.", color: "bg-sky-400" },
  { num: "03", Icon: Wifi, title: "Wi-Fi & connectivity", desc: "Wi-Fi, router, and internet connectivity problems.", color: "bg-sky-500" },
  { num: "04", Icon: Printer, title: "Printer & scanning support", desc: "Setup, scanning issues, driver problems, network printers.", color: "bg-[#0b0a3d]" },
];

const ISSUES_RIGHT = [
  { num: "05", Icon: Hexagon, title: "Software installs & updates", desc: "Software installs, updates, licensing, and configuration.", color: "bg-[#0b0a3d]" },
  { num: "06", Icon: FolderOpen, title: "File sharing & permissions", desc: "File sharing, permissions, and shared drive access issues.", color: "bg-sky-400" },
  { num: "07", Icon: User, title: "Device setup & user changes", desc: "New device setup, user onboarding support.", color: "bg-sky-500" },
  { num: "08", Icon: ShieldAlert, title: "Malware & security hygiene", desc: "Malware cleanup guidance and security hygiene improvements.", color: "bg-sky-400" },
];

function CommonIssuesSection() {
  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
            Common Issues We Fix
          </h2>
          <p className="mt-6 text-slate-500 leading-relaxed">
            So much has to be working in concert to stay as productive as possible. We make sure they all function as they are supposed to.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-[1fr_auto_1fr] gap-6 lg:gap-10 items-center">
          {/* Left column */}
          <div className="flex flex-col gap-5">
            {ISSUES_LEFT.map(({ num, Icon, title, desc, color }) => (
              <div key={num} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white shadow-sm pl-2 pr-6 py-3">
                <div className={`w-12 h-12 rounded-xl ${color} text-white flex items-center justify-center shrink-0`}>
                  <Icon className="w-6 h-6" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-[#0b0a3d] leading-tight">{title}</h3>
                  <p className="mt-1 text-xs text-slate-500 leading-snug">{desc}</p>
                </div>
                <span className="text-2xl font-extrabold text-sky-400 tabular-nums">{num}</span>
              </div>
            ))}
          </div>

          {/* Center card */}
          <div className="hidden lg:flex flex-col items-center justify-center rounded-2xl bg-gradient-to-b from-sky-50 to-white border border-sky-100 p-8 w-56">
            <div className="w-28 h-28 rounded-full bg-gradient-to-br from-sky-100 to-sky-200 flex items-center justify-center">
              <Laptop className="w-14 h-14 text-sky-500" strokeWidth={1.5} />
            </div>
            <p className="mt-6 text-sm font-semibold text-slate-600 text-center">
              Tech support coverage
            </p>
          </div>

          {/* Right column */}
          <div className="flex flex-col gap-5">
            {ISSUES_RIGHT.map(({ num, Icon, title, desc, color }) => (
              <div key={num} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white shadow-sm pl-6 pr-2 py-3">
                <span className="text-2xl font-extrabold text-[#0b0a3d] tabular-nums">{num}</span>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-[#0b0a3d] leading-tight">{title}</h3>
                  <p className="mt-1 text-xs text-slate-500 leading-snug">{desc}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl ${color} text-white flex items-center justify-center shrink-0`}>
                  <Icon className="w-6 h-6" strokeWidth={2} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const VOIP_ISSUES = [
  { num: "01", title: "Dropped calls & bad call quality", desc: "Network tuning, QoS configuration, bandwidth checks, and handset replacements." },
  { num: "02", title: "Confusing or broken call routing", desc: "Auto-attendants, ring groups, after-hours flows, and voicemail-to-email built around how your team actually works." },
  { num: "03", title: "Phone bills that keep climbing", desc: "Provider audits and re-quotes across our 50+ partners — most clients save 30–60% versus their old landline or current VoIP plan." },
  { num: "04", title: "Remote & hybrid teams without unified phones", desc: "Mobile apps, softphones, and BYOD setup so every team member rings on one business number from anywhere." },
  { num: "05", title: "Compliance gaps (HIPAA, financial, legal)", desc: "Call recording controls, encryption, BAAs, and audit trails configured to your industry's requirements." },
  { num: "06", title: "Integration with your CRM & tools", desc: "Click-to-dial, screen pops, and call logging tied into Salesforce, HubSpot, Microsoft 365, Google Workspace, and most major industry CRMs." },
];

const VOIP_PROVIDERS = [
  "RingCentral", "Zoom Phone", "Spectrum Business", "Nextiva", "8x8",
  "Vonage", "Ooma Office", "GoTo Connect", "Dialpad", "Intermedia", "Mitel", "+ 40 more",
];

const VOIP_WHAT_WE_MEAN = [
  { title: "New VoIP Setup", body: "We assess your call volume, team size, and software, then recommend and install the right provider — handsets, mobile apps, and number porting included." },
  { title: "Switching Providers", body: "Already on VoIP but unhappy? We migrate you to a better-fit platform with zero downtime, port your existing numbers, and rebuild your call flows." },
  { title: "Ongoing VoIP Support", body: "Phone trouble shouldn't require three calls to an offshore queue. Get a local team that knows your setup and answers when you call." },
];

const VOIP_STATS = [
  { stat: "50+", label: "Providers", body: "Vendor-neutral partnerships across the VoIP industry." },
  { stat: "30–60%", label: "Savings", body: "Typical phone-bill reduction for clients switching from landline or legacy VoIP." },
  { stat: "Zero", label: "Downtime", body: "We migrate during off-hours and port numbers without losing a call." },
];

function VoipExtrasSection() {
  return (
    <>
      {/* What We Mean By VoIP */}
      <section className="relative py-20 md:py-28">
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              What We Mean By VoIP
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {VOIP_WHAT_WE_MEAN.map(({ title, body }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                <h3 className="text-xl font-bold text-[#0b0a3d]">{title}</h3>
                <p className="mt-4 text-sm text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
          <div className="mt-14 text-center">
            <Link
              to="/contact"
              className="inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
            >
              FREE CONSULTATION
            </Link>
          </div>
        </div>
      </section>

      {/* Common VoIP Issues */}
      <section className="relative py-20 md:py-28">
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              Common Issues We Fix
            </h2>
            <p className="mt-6 text-slate-500 leading-relaxed">
              From dropped calls to climbing phone bills — here's where we step in.
            </p>
          </div>
          <div className="mt-14 grid md:grid-cols-2 gap-5">
            {VOIP_ISSUES.map(({ num, title, desc }) => (
              <div key={num} className="flex gap-5 rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                <span className="text-3xl font-extrabold text-sky-400 tabular-nums shrink-0">{num}</span>
                <div>
                  <h3 className="text-base font-bold text-[#0b0a3d] leading-tight">{title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 50+ Providers */}
      <section className="relative py-20 md:py-28">
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              50+ VoIP Providers. One Trusted Partner.
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              We're not loyal to a single carrier — we're loyal to you. SimpleTech partners with every major VoIP platform, so we recommend whichever one actually fits your business.
            </p>
            <p className="mt-4 text-sm text-slate-500 leading-relaxed">
              Most "VoIP companies" only sell one product, which means you get the same recommendation no matter your situation. Because we're partnered with 50+ providers, we run side-by-side comparisons based on your call volume, team size, software, and budget — and walk you through the trade-offs in plain English.
            </p>
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            {VOIP_PROVIDERS.map((p) => (
              <span key={p} className="px-5 py-2 rounded-full border border-slate-200 bg-white text-sm font-semibold text-[#0b0a3d] shadow-sm">
                {p}
              </span>
            ))}
          </div>
          <div className="mt-14 text-center">
            <Link
              to="/contact"
              className="inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
            >
              COMPARE PROVIDERS FOR MY BUSINESS
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative py-16 md:py-20">
        <div className="relative max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-6">
          {VOIP_STATS.map(({ stat, label, body }) => (
            <div key={label} className="rounded-2xl bg-[#0b0a3d] text-white p-8 text-center">
              <p className="text-4xl md:text-5xl font-extrabold text-sky-400">{stat}</p>
              <p className="mt-2 text-base font-bold tracking-wider">{label}</p>
              <p className="mt-4 text-sm text-white/80 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

const INTEGRATION_CATEGORIES: { title: string; tools: string[] }[] = [
  {
    title: "CRM & Sales",
    tools: ["Salesforce", "HubSpot", "Zoho CRM", "Pipedrive", "GoHighLevel", "Monday.com", "Keap", "Close"],
  },
  {
    title: "Accounting & Payments",
    tools: ["QuickBooks", "Xero", "FreshBooks", "Stripe", "Square", "PayPal", "Wave", "Bill.com"],
  },
  {
    title: "Productivity & Communication",
    tools: ["Microsoft 365", "Google Workspace", "Slack", "Microsoft Teams", "Notion", "Asana", "ClickUp", "Trello"],
  },
  {
    title: "Marketing & Email",
    tools: ["Mailchimp", "ActiveCampaign", "Constant Contact", "Klaviyo", "ConvertKit", "Brevo", "Meta Ads", "Google Ads"],
  },
  {
    title: "Scheduling, Forms & Storage",
    tools: ["Calendly", "Acuity", "Jotform", "Typeform", "Google Drive", "Dropbox", "OneDrive", "DocuSign"],
  },
];

function IntegrationToolsSection() {
  return (
    <section className="relative py-20 md:py-28">
      <div className="relative max-w-6xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
            100+ Apps We Already Know How to Connect
          </h2>
          <p className="mt-6 text-slate-600 leading-relaxed">
            We work across every category of small business software. If your tools have an API or a Zapier integration, we can wire them together.
          </p>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {INTEGRATION_CATEGORIES.map(({ title, tools }) => (
            <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-bold text-[#0b0a3d]">{title}</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {tools.map((t) => (
                  <span key={t} className="px-3 py-1 rounded-full border border-slate-200 bg-slate-50 text-xs font-semibold text-[#0b0a3d]">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-14 text-center">
          <Link
            to="/contact"
            className="inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
          >
            DON'T SEE YOUR TOOL? ASK US.
          </Link>
        </div>
      </div>
    </section>
  );
}

const SENIORS_HELP_CATEGORIES: { title: string; items: string[] }[] = [
  {
    title: "Phones & Tablets",
    items: [
      "iPhone, iPad & Android setup and tutoring",
      "FaceTime, Zoom, and video calls with family",
      "Texting, photos, contacts, and voicemail",
      "Apps (banking, Medicare, pharmacy, food delivery)",
    ],
  },
  {
    title: "Computers & Laptops",
    items: [
      "Slow or frozen computer fixes",
      "Windows or Mac setup and updates",
      "Printer setup and troubleshooting",
      "File backups so you never lose photos or documents",
    ],
  },
  {
    title: "Internet, Email & Wi-Fi",
    items: [
      "Wi-Fi setup and password help",
      "Email setup (Gmail, Outlook, Yahoo, AOL)",
      "Streaming TV setup (Netflix, YouTube, Roku, Apple TV)",
      "Smart speakers (Alexa, Google Home)",
    ],
  },
  {
    title: "Online Safety",
    items: [
      "Spotting and avoiding scam phone calls, emails, and texts",
      "Strong passwords made simple (with a notebook system if you prefer)",
      "Recovering hacked email, Facebook, or bank accounts",
      "Removing scam pop-ups and fake 'tech support' messages",
    ],
  },
  {
    title: "One-on-One Tech Tutoring",
    items: [
      "'I just got this phone — can you show me how to use it?'",
      "Photo organizing and sharing with the family",
      "Online shopping safely (Amazon, Walmart, etc.)",
      "Telehealth appointment setup",
    ],
  },
];

function SeniorsExtrasSection() {
  return (
    <>
      {/* Adult children callout */}
      <section className="relative py-16 md:py-20">
        <div className="relative max-w-5xl mx-auto px-6">
          <div className="rounded-2xl border border-sky-100 bg-sky-50 p-8 md:p-12 shadow-sm">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <img
                src={seniorsHero}
                alt="Patient technician helping a senior with his smartphone"
                className="w-full h-auto rounded-xl object-cover"
                loading="lazy"
              />
              <div className="flex items-start gap-5">
                <Users className="w-10 h-10 text-sky-500 shrink-0 hidden md:block" strokeWidth={1.8} />
                <div>
                  <h2 className="text-2xl md:text-4xl font-extrabold text-[#0b0a3d] tracking-tight">
                    Looking for Tech Help for Your Mom, Dad, or Grandparent?
                  </h2>
                  <p className="mt-4 text-base md:text-lg text-slate-700 leading-relaxed">
                    You're not alone — most of the people who call us are adult children worried about a parent struggling with technology, falling for scams, or feeling left behind. We're patient, never condescending, and we take the time your parent deserves. You can be on the call, in the room, or completely hands-off — whatever works best for your family. We also offer monthly check-in plans so you don't have to be the IT support on top of everything else you're juggling.
                  </p>
                  <div className="mt-6">
                    <Link
                      to="/contact"
                      className="inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
                    >
                      SCHEDULE A FREE 15-MINUTE CALL
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Help Seniors With */}
      <section className="relative py-20 md:py-28">
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              What We Help Seniors With
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
              From a brand-new iPhone to a frozen laptop to a suspicious email — here's what we help with every week.
            </p>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SENIORS_HELP_CATEGORIES.map(({ title, items }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
                <h3 className="text-lg font-bold text-[#0b0a3d]">{title}</h3>
                <ul className="mt-4 space-y-2.5 text-base text-slate-700 leading-relaxed">
                  {items.map((it) => (
                    <li key={it} className="flex gap-2">
                      <span className="text-sky-500 mt-1">•</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

const CYBER_THREATS: { title: string; body: string }[] = [
  { title: "Ransomware", body: "Endpoint protection, immutable backups, and 24/7 monitoring stop ransomware before it spreads — and let you recover without paying." },
  { title: "Phishing & Email Scams", body: "Advanced email filtering, link sandboxing, and quarterly phishing simulations train your team to spot the 90% of attacks that start in the inbox." },
  { title: "Business Email Compromise", body: "MFA, conditional access policies, and wire-fraud safeguards protect against the #1 financial-loss attack hitting law firms, realtors, and financial offices." },
  { title: "Credential Theft", body: "Dark-web monitoring, password manager rollouts, and MFA enforcement — so a leaked password doesn't become a breach." },
  { title: "Insider Mistakes", body: "Most breaches start with a well-meaning employee clicking the wrong thing. Continuous training keeps your team sharp." },
  { title: "Compliance Failures", body: "HIPAA, PCI, SEC, FINRA, CCPA — we map your controls to the framework you need and keep you audit-ready." },
];

const CYBER_INDUSTRIES: { title: string; body: string }[] = [
  { title: "Healthcare & Dental", body: "HIPAA Security Rule compliance, encrypted email, secure file storage, BAAs, audit logs, and breach response readiness." },
  { title: "Legal & Estate Planning", body: "Client confidentiality, secure document portals, state-bar safeguards, and wire-fraud protection for trust accounts." },
  { title: "Financial & Insurance", body: "SEC, FINRA, and California Department of Insurance controls, encrypted communications, and supervised email retention." },
  { title: "Real Estate", body: "Wire-fraud protection (the #1 financial loss in real estate), MLS account security, and transaction-coordinator training." },
  { title: "Professional Services", body: "Vendor questionnaires, SOC-2-aligned controls, and security packages priced for non-tech firms." },
  { title: "E-Commerce & Retail", body: "PCI-DSS compliance, payment-processor configuration, and fraud monitoring." },
];

function CyberExtrasSection() {
  return (
    <>
      {/* Threats We Defend Against */}
      <section className="relative py-20 md:py-28">
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              What We Defend Against
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              The threats that take down small businesses have names. Here are the ones we stop every week.
            </p>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CYBER_THREATS.map(({ title, body }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
                <ShieldAlert className="w-9 h-9 text-sky-500" strokeWidth={1.6} />
                <h3 className="mt-4 text-lg font-bold text-[#0b0a3d]">{title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Secure */}
      <section className="relative py-20 md:py-28">
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              Cybersecurity Built for Your Industry
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              We specialize in industries where one breach means more than lost time — it means fines, lawsuits, and lost clients.
            </p>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CYBER_INDUSTRIES.map(({ title, body }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
                <h3 className="text-lg font-bold text-[#0b0a3d]">{title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
          <div className="mt-14 text-center">
            <Link
              to="/contact"
              className="inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
            >
              FREE RISK ASSESSMENT
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

